import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { emailsService } from '../../services/emailsService';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import '../Cards/CardsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const EMPTY_EMAIL_FORM = {
  username: '',
  name: '',
  email: '',
  email_password: '',
  email_type: 'Gmail',
  email_mobile: '',
  status: 'Active',
};

const EMAIL_TYPES = ['Gmail', 'Yahoo', 'Outlook', 'Custom', 'GMX'];
const STATUS_TYPES = ['Active', 'Closed', 'Paused'];
const USERNAME_COL_WIDTH = 78;
const STATUS_COL_WIDTH = 92;

const toUiEmailType = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'gmx') return 'GMX';
  if (normalized === 'yahoo') return 'Yahoo';
  if (normalized === 'outlook') return 'Outlook';
  if (normalized === 'custom') return 'Custom';
  return 'Gmail';
};

const toApiEmailType = (value) => String(value || 'Gmail').toLowerCase();

const toUiStatus = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'inactive') return 'Closed';
  if (normalized === 'paused') return 'Paused';
  return 'Active';
};

const toApiStatus = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'closed') return 'inactive';
  if (normalized === 'paused') return 'paused';
  return 'active';
};

const normalizeEmailItem = (item) => ({
  ...item,
  email_type: toUiEmailType(item.email_type),
  status: toUiStatus(item.status),
});

const EmailsPage = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_EMAIL_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  const loadEmails = async (qValue = '') => {
    setLoading(true);
    setError('');
    const result = await emailsService.listEmails({
      q: qValue || undefined,
      limit: 5000,
    });

    if (result.success) {
      setEmails((result.items || []).map(normalizeEmailItem));
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadEmails('');
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'User',
      field: 'username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      pinned: 'left',
      editable: true,
    },
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 180,
      editable: true,
    },
    {
      headerName: 'Parent',
      field: 'parent',
      minWidth: 120,
      editable: false,
      valueGetter: () => '-',
    },
    {
      headerName: 'Email',
      field: 'email',
      minWidth: 260,
      flex: 1.4,
      editable: true,
    },
    {
      headerName: 'Password',
      field: 'email_password',
      minWidth: 180,
      editable: true,
    },
    {
      headerName: 'Type',
      field: 'email_type',
      minWidth: 130,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: EMAIL_TYPES,
      },
    },
    {
      headerName: 'Mobile',
      field: 'email_mobile',
      minWidth: 170,
      editable: true,
    },
    {
      headerName: 'DOB',
      field: 'dob',
      minWidth: 140,
      editable: true,
      cellDataType: 'dateString',
      cellEditor: 'agDateStringCellEditor',
    },
    {
      headerName: 'Address',
      field: 'address',
      minWidth: 240,
      flex: 1,
      editable: true,
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: STATUS_COL_WIDTH,
      width: STATUS_COL_WIDTH,
      maxWidth: STATUS_COL_WIDTH,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: STATUS_TYPES,
      },
      cellClassRules: {
        'status-cell-active': (params) => String(params.value || '').toLowerCase() === 'active',
        'status-cell-closed': (params) => ['closed', 'paused'].includes(String(params.value || '').toLowerCase()),
      },
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const handleFilter = (event) => {
    event.preventDefault();
    loadEmails(query.trim());
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_EMAIL_FORM);
    setCreateError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSaving) return;
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const normalizeOptionalText = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };

  const handleCreateEmail = async (event) => {
    event.preventDefault();
    const username = createForm.username.trim();
    const email = createForm.email.trim();

    if (!username || !email) {
      setCreateError('User and Email are required.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');
    const result = await emailsService.createEmail({
      username,
      name: normalizeOptionalText(createForm.name),
      email,
      email_password: normalizeOptionalText(createForm.email_password),
      email_type: toApiEmailType(email.toLowerCase().endsWith('@gmx.com') ? 'GMX' : createForm.email_type),
      email_mobile: normalizeOptionalText(createForm.email_mobile),
      status: toApiStatus(createForm.status),
    });
    setCreateSaving(false);

    if (!result.success) {
      setCreateError(result.error);
      return;
    }

    setEmails((current) => [normalizeEmailItem(result.item), ...current]);
    setTotalCount((current) => current + 1);
    setMessage(result.message || 'Email created.');
    setError('');
    setShowCreateModal(false);
  };

  const handleCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    const field = colDef.field;

    if (!field || oldValue === newValue) {
      return;
    }

    const requiredFields = new Set(['username', 'name', 'email']);
    const normalizedValue = typeof newValue === 'string' ? newValue.trim() : newValue;
    if (requiredFields.has(field) && !normalizedValue) {
      node.setDataValue(field, oldValue);
      setError(`${colDef.headerName} cannot be empty.`);
      return;
    }

    const result = await emailsService.updateEmail(data.user_id, {
      [field]: field === 'status'
        ? toApiStatus(normalizedValue)
        : field === 'email_type'
          ? toApiEmailType(normalizedValue)
          : (normalizedValue === '' ? null : normalizedValue),
    });

    if (!result.success) {
      node.setDataValue(field, oldValue);
      setError(result.error);
      return;
    }

    setError('');
    setMessage(result.message || 'User updated successfully');
    setEmails((current) => current.map((item) => (
      item.user_id === result.item.user_id ? normalizeEmailItem(result.item) : item
    )));
    node.setData(normalizeEmailItem(result.item));
  };

  return (
    <div className="venues-page cards-page resource-page-with-sidebar">
      <ResourceSidebar />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>Emails</h1>
          <div className="venues-header-actions">
            <button className="btn btn-primary" type="button" onClick={openCreateModal}>
              Add Email
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        <form className="venues-filter-form" onSubmit={handleFilter}>
          <div className="form-group">
            <label htmlFor="emails-query">Search</label>
            <input
              id="emails-query"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="user, name, email, mobile, status..."
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>

        <div className="venues-summary">
          <strong>Showing:</strong> {emails.length} emails
          {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading emails...</p>
          </div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid">
              <AgGridReact
                theme="legacy"
                rowData={emails}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                alwaysShowHorizontalScroll={true}
                pagination={true}
                paginationPageSize={100}
                getRowId={(params) => params.data.user_id}
                onCellValueChanged={handleCellValueChanged}
              />
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="venues-modal-backdrop" onMouseDown={closeCreateModal}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Add Email</h2>
                <button className="venues-modal-close" type="button" onClick={closeCreateModal} disabled={createSaving}>
                  x
                </button>
              </div>

              <form className="venues-modal-form" onSubmit={handleCreateEmail}>
                <div className="form-group">
                  <label htmlFor="email-username">User</label>
                  <input
                    id="email-username"
                    type="text"
                    value={createForm.username}
                    onChange={(event) => handleCreateFormChange('username', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-name">Name</label>
                  <input
                    id="email-name"
                    type="text"
                    value={createForm.name}
                    onChange={(event) => handleCreateFormChange('name', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-address">Email</label>
                  <input
                    id="email-address"
                    type="email"
                    value={createForm.email}
                    onChange={(event) => handleCreateFormChange('email', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-password">Password</label>
                  <input
                    id="email-password"
                    type="text"
                    value={createForm.email_password}
                    onChange={(event) => handleCreateFormChange('email_password', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-type">Type</label>
                  <select
                    id="email-type"
                    value={createForm.email_type}
                    onChange={(event) => handleCreateFormChange('email_type', event.target.value)}
                    disabled={createSaving}
                  >
                    {EMAIL_TYPES.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="email-mobile">Mobile</label>
                  <input
                    id="email-mobile"
                    type="text"
                    value={createForm.email_mobile}
                    onChange={(event) => handleCreateFormChange('email_mobile', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email-status">Status</label>
                  <select
                    id="email-status"
                    value={createForm.status}
                    onChange={(event) => handleCreateFormChange('status', event.target.value)}
                    disabled={createSaving}
                  >
                    {STATUS_TYPES.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </div>

                {createError && <div className="notification error">{createError}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateModal} disabled={createSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createSaving}>
                    {createSaving ? 'Saving...' : 'Save Email'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmailsPage;
